import apiClient, { readSSEStream, getAuthHeader } from './apiClient';

class UserService {
  // Get all users
  async getAllUsers(options = {}) {
    try {
      const { 
        search, 
        participantsOnly, 
        nonParticipantsOnly, 
        page = 1, 
        limit = 10 
      } = options;
      
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (participantsOnly) params.append('participantsOnly', 'true');
      if (nonParticipantsOnly) params.append('nonParticipantsOnly', 'true');
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      const queryString = params.toString();
      const url = queryString ? `/users?${queryString}` : '/users';
      
      const response = await apiClient.get(url);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get all users error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch users'
      };
    }
  }

  // Get user by ID
  async getUserById(userId) {
    try {
      const response = await apiClient.get(`/users/${userId}`);
      console.log('UserService - getUserById response:', response.data);
      
      const result = {
        success: true,
        data: response.data?.data || response.data  // Handle both formats
      };
      
      console.log('UserService - processed result:', result);
      return result;
    } catch (error) {
      console.error('Get user by ID error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch user'
      };
    }
  }

  // Get participant by ID (using participant-specific endpoint)
  async getParticipantById(participantId) {
    try {
      const response = await apiClient.get(`/users/participants/${participantId}`);
      console.log('UserService - getParticipantById response:', response.data);
      
      const result = {
        success: true,
        data: response.data?.data || response.data  // Handle both formats
      };
      
      console.log('UserService - processed participant result:', result);
      return result;
    } catch (error) {
      console.error('Get participant by ID error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch participant'
      };
    }
  }

  // Create new user
  async createUser(userData) {
    try {
      const response = await apiClient.post('/users', userData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Create user error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create user'
      };
    }
  }

  // Update user
  async updateUser(userId, userData) {
    try {
      const response = await apiClient.put(`/users/${userId}`, userData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Update user error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update user'
      };
    }
  }

  // Delete user
  async deleteUser(userId) {
    try {
      const response = await apiClient.delete(`/users/${userId}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Delete user error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete user'
      };
    }
  }

  // Update user role
  async updateUserRole(userId, role) {
    try {
      const response = await apiClient.patch(`/users/${userId}/role`, { role });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Update user role error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update user role'
      };
    }
  }

  // Get users by role
  async getUsersByRole(role) {
    try {
      const response = await apiClient.get(`/users?role=${role}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get users by role error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch users by role'
      };
    }
  }

  // Search users
  async searchUsers(searchTerm) {
    try {
      const response = await apiClient.get(`/users/search?q=${encodeURIComponent(searchTerm)}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Search users error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to search users'
      };
    }
  }

  // Send user invitation
  async sendInvitation(email, groupId) {
    try {
      const response = await apiClient.post('/users/invite', {
        email,
        groupId
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Send invitation error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send invitation'
      };
    }
  }

  // Get invitation details
  async getInvitationDetails(token) {
    try {
      const response = await apiClient.get(`/users/invitation/${token}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get invitation details error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to get invitation details'
      };
    }
  }

  // Complete user registration
  async completeRegistration(data) {
    try {
      const response = await apiClient.post('/users/register', data);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Complete registration error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to complete registration'
      };
    }
  }

  // Get user statistics
  async getUserStats() {
    try {
      const response = await apiClient.get('/users/stats');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get user stats error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch user statistics'
      };
    }
  }

  // Resend invitation for expired tokens
  async resendInvitation(email) {
    try {
      const response = await apiClient.post('/users/resend-invitation', { email });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Resend invitation error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to resend invitation'
      };
    }
  }

  // Forgot password - send reset email
  async forgotPassword(email) {
    try {
      const response = await apiClient.post('/users/forgot-password', { email });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Forgot password error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to send password reset email'
      };
    }
  }

  // Reset password with token
  async resetPassword(token, password) {
    try {
      const response = await apiClient.post('/users/reset-password', { token, password });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Reset password error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to reset password'
      };
    }
  }

  // Get participant invitation details
  async getParticipantInvitationDetails(token) {
    try {
      const response = await apiClient.get(`/users/participants/invitation/${token}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Get participant invitation details error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch invitation details'
      };
    }
  }

  // Complete participant registration
  async completeParticipantRegistration(token, userData) {
    try {
      const response = await apiClient.post('/users/participants/register', {
        token,
        ...userData
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Complete participant registration error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to complete registration'
      };
    }
  }

  // Update participant
  async updateParticipant(participantId, userData) {
    try {
      console.log('UserService - updateParticipant called with ID:', participantId);
      console.log('UserService - updateParticipant userData:', userData);
      console.log('UserService - making PUT request to:', `/users/participants/${participantId}`);
      
      const response = await apiClient.put(`/users/participants/${participantId}`, userData);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Update participant error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update participant'
      };
    }
  }

  // Upload participants CSV
  async uploadParticipantsCSV(formData) {
    try {
      const response = await apiClient.post('/users/participants/upload-csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return {
        success: true,
        data: response.data,
        message: response.data.message
      };
    } catch (error) {
      console.error('Upload CSV error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to upload CSV file'
      };
    }
  }

  // Search users for SMS/WhatsApp recipient selection
  async searchUsersForMessaging(query) {
    try {
      if (!query || query.length < 2) {
        return {
          success: true,
          data: []
        };
      }

      const params = new URLSearchParams();
      params.append('q', query);

      const response = await apiClient.get(`/sms/search-users?${params.toString()}`);
      return {
        success: true,
        data: response.data?.users || response.data || []
      };
    } catch (error) {
      console.error('Search users for messaging error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to search users',
        data: []
      };
    }
  }

  // Get campaigns for a participant
  async getParticipantCampaigns(participantId) {
    try {
      const response = await apiClient.get(`/users/participants/${participantId}/campaigns`);
      return {
        success: true,
        data: response.data?.data || []
      };
    } catch (error) {
      console.error('Get participant campaigns error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch participant campaigns',
        data: []
      };
    }
  }

  // Get AI profile insights for a participant (longer timeout for AI processing)
  // Stream AI insights for a participant token by token.
  // callbacks: { onToken(token), onDone(meta), onError(msg) }
  async getParticipantAIInsights(participantId, callbacks, period = 'all', startDate = null, endDate = null) {
    try {
      let url = `/api/users/participants/${participantId}/ai-insights?period=${period}`;
      if (period === 'custom' && startDate && endDate) {
        url += `&startDate=${startDate}&endDate=${endDate}`;
      }
      const response = await fetch(url, { headers: { Authorization: getAuthHeader(), Accept: 'text/event-stream' } });
      await readSSEStream(response, callbacks);
    } catch (error) {
      console.error('Get participant AI insights error:', error);
      callbacks.onError?.(error.message || 'Failed to get AI insights');
    }
  }

  // Stream a follow-up question response token by token.
  // callbacks: { onToken(token), onDone(meta), onError(msg) }
  async askParticipantAIQuestion(participantId, sessionId, question, callbacks) {
    try {
      const response = await fetch(`/api/users/participants/${participantId}/ai-insights/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: getAuthHeader(), Accept: 'text/event-stream' },
        body: JSON.stringify({ sessionId, question })
      });
      if (response.status === 410) {
        callbacks.onError?.('expired');
        return;
      }
      await readSSEStream(response, callbacks);
    } catch (error) {
      console.error('Ask participant AI question error:', error);
      callbacks.onError?.(error.message || 'Failed to process question');
    }
  }

  // End a participant AI insights session
  async endParticipantAISession(participantId, sessionId) {
    try {
      await apiClient.delete(`/users/participants/${participantId}/ai-insights/session/${sessionId}`, {
        timeout: 10000
      });
    } catch (error) {
      // Silently ignore cleanup errors
      console.warn('End AI session error (non-critical):', error.message);
    }
  }
}

// Create and export a singleton instance
const userService = new UserService();
export default userService;